import React from 'react'
import type { VehicleInfo, DragState } from './types'
import { vehicleLabel, vehicleColorHex } from './types'

type FitSummaryMap = Record<string, { status: string; warnings: string[]; riskFlags: Record<string, boolean>; dims: { lengthIn: number|null; heightIn: number|null; curbWeightLb: number|null; wheelbaseIn: number|null } }>

interface VehicleQueueProps {
  vehicles: VehicleInfo[]
  drag: DragState | null
  onDragStart: (e: React.PointerEvent, vehicleId: string) => void
  selectedVehicleId?: string | null
  onSelectVehicle?: (vehicleId: string) => void
  readOnly?: boolean
  fitSummary?: FitSummaryMap
}

function FitBadge({ status }: { status: string }) {
  if (status === 'ready') return (
    <span className="inline-flex items-center gap-0.5 text-[9px] font-medium text-green-700 bg-green-50 border border-green-200 rounded px-1 py-0.5">
      <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />fit ✓
    </span>
  )
  if (status === 'fallback') return (
    <span className="inline-flex items-center gap-0.5 text-[9px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 py-0.5">
      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />partial
    </span>
  )
  return (
    <span className="inline-flex items-center gap-0.5 text-[9px] font-medium text-gray-500 bg-gray-50 border border-gray-200 rounded px-1 py-0.5">
      <span className="w-1.5 h-1.5 rounded-full bg-gray-300 inline-block" />no profile
    </span>
  )
}

function VehicleCard({ vehicle, isDragging, onDragStart, onSelectVehicle, selected, readOnly, fitEntry }: {
  vehicle: VehicleInfo
  isDragging: boolean
  onDragStart: (e: React.PointerEvent, vehicleId: string) => void
  onSelectVehicle?: (vehicleId: string) => void
  selected: boolean
  readOnly: boolean
  fitEntry?: FitSummaryMap[string]
}) {
  const color = vehicleColorHex(vehicle.color)
  const flags = fitEntry?.riskFlags ?? {}
  const riskList = [
    flags.isTallVehicle && 'Tall',
    flags.isLowClearance && 'Low GC',
    flags.isLongWheelbase && 'Long WB',
    flags.isHeavyVehicle && 'Heavy',
    flags.isOversize && 'Oversize',
  ].filter(Boolean) as string[]

  return (
    <button
      type="button"
      className={[
        'w-full text-left rounded-lg border p-2.5 min-h-[44px] transition-all',
        isDragging
          ? 'opacity-40 border-dashed border-blue-300 bg-blue-50'
          : readOnly
          ? 'bg-white border-gray-200'
          : selected
          ? 'bg-blue-50 border-blue-400 ring-2 ring-blue-200'
          : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-sm cursor-grab active:cursor-grabbing touch-none select-none',
      ].join(' ')}
      aria-pressed={selected}
      onPointerDown={readOnly ? undefined : (e) => {
        if (e.pointerType === 'touch') return
        onDragStart(e, vehicle.vehicleId)
      }}
      onClick={readOnly ? undefined : () => onSelectVehicle?.(vehicle.vehicleId)}
      style={{ borderLeftColor: color, borderLeftWidth: 3 }}
    >
      <div className="flex items-start justify-between gap-1">
        <div className="text-sm font-semibold text-gray-800 leading-tight flex-1">{vehicleLabel(vehicle)}</div>
        {fitEntry && <FitBadge status={fitEntry.status} />}
      </div>
      <div className="text-xs text-gray-400 font-mono mt-0.5">VIN …{vehicle.vin.slice(-6)}</div>
      {vehicle.color && (
        <div className="flex items-center gap-1 mt-1">
          <span className="inline-block w-2.5 h-2.5 rounded-full border border-gray-300" style={{ background: color }} />
          <span className="text-[10px] text-gray-400 capitalize">{vehicle.color}</span>
        </div>
      )}
      {fitEntry && fitEntry.dims.heightIn && (
        <div className="text-[9px] text-gray-400 mt-1">
          {fitEntry.dims.lengthIn ? `${Math.round(fitEntry.dims.lengthIn)}"L ` : ''}
          {`${Math.round(fitEntry.dims.heightIn)}"H `}
          {fitEntry.dims.curbWeightLb ? `${Math.round(fitEntry.dims.curbWeightLb).toLocaleString()}lb` : ''}
        </div>
      )}
      {riskList.length > 0 && (
        <div className="flex flex-wrap gap-0.5 mt-1">
          {riskList.map(r => (
            <span key={r} className="text-[8px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-1">⚠ {r}</span>
          ))}
        </div>
      )}
    </button>
  )
}

export function VehicleQueue({ vehicles, drag, onDragStart, selectedVehicleId, onSelectVehicle, readOnly = false, fitSummary }: VehicleQueueProps) {
  if (vehicles.length === 0) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
        <div className="text-lg mb-1">✅</div>
        <div className="text-sm font-semibold text-green-800">All vehicles placed</div>
        <div className="text-xs text-green-600 mt-1">Save layout to finalize</div>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
        Unplaced ({vehicles.length})
      </div>
      {vehicles.map(v => (
        <VehicleCard
          key={v.vehicleId}
          vehicle={v}
          isDragging={drag?.vehicleId === v.vehicleId && drag?.sourceSlotId === null}
          onDragStart={onDragStart}
          onSelectVehicle={onSelectVehicle}
          selected={selectedVehicleId === v.vehicleId}
          readOnly={readOnly}
          fitEntry={fitSummary?.[v.vehicleId]}
        />
      ))}
      {!readOnly && (
        <div className="text-[10px] text-gray-400 text-center pt-1">
          Drag vehicles onto trailer slots →
        </div>
      )}
    </div>
  )
}
